import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { useQueryState } from 'react-router-use-location-state';
import { Box, Grid, InternalGrid, ThemeDefault } from '@paygreen/paygreen-ui';
import { useAPIError, useMode } from '../../../context';
import {
    H1Title,
    ModalIcon,
    NoDataFoundMessage,
    ToggleMode,
} from '../../../components/';
import { startOfMonth, endOfMonth } from '../../../utils/constants';
import {
    convertCarbonReports,
    formatDateApi,
    IsDesktop,
} from '../../../utils/tools';
import { useNodeDimensions } from '../../../utils/hooks';
import { getCarbonReports } from '../service';
import DashboardCardCO2Histogram from './DashboardCardCO2Histogram';
import DashboardFilter from './DashboardFilter';
import DashboardCardCO2Emitted from './DashboardCardCO2Emitted';
import CarbonEquivalenceModal from './shared/CarbonEquivalenceModal';

const Dashboard = ({ t }) => {
    const { addError } = useAPIError();
    const { mode } = useMode();

    const [reportsData, setReportsData] = useState();
    const [histogramData, setHistogramData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [startDate, setStartDate] = useQueryState('startDate', startOfMonth);
    const [endDate, setEndDate] = useQueryState('endDate', endOfMonth);
    const [noDataMessage, setNoDataMessage] = useState();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    // we create a ref using a special hook to get width from main Grid component
    const [ref, { width }] = useNodeDimensions();

    /**
     * @description to verify data from API in case of corrupted data in RECETTE environment
     * @param {object} data - carbon reports object received from API
     */
    const checkCarbonReports = useCallback(
        (data) => {
            if (
                data &&
                (Number(data.carbonEmitted) === 0 ||
                    Number(data.carbonEmitted) < Number(data.carbonOffset))
            ) {
                isMounted.current &&
                    setNoDataMessage(t('dashboard.noDataFound'));
                return null;
            } else {
                return data;
            }
        },
        [t],
    );

    useEffect(() => {
        isMounted.current = true;

        const params = {
            begin: formatDateApi(startDate),
            end: formatDateApi(endDate),
        };

        if (isMounted.current) {
            setIsLoading(true);
            setNoDataMessage();
        }

        const fetchGlobalData = getCarbonReports(params, mode)
            .then((res) => {
                return res.data;
            })
            .catch((error) => {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
            });

        const fetchHistogramData = getCarbonReports({ ...params, daily: 1 }, mode)
            .then((res) => {
                return res.data.daily;
            })
            .catch((error) => {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
            });

        Promise.all([fetchGlobalData, fetchHistogramData]).then((res) => {
            if (isMounted.current) {
                setReportsData(checkCarbonReports(res[0]) || null);

                setHistogramData(checkCarbonReports(res[1]) || null);
                setIsLoading(false);
            }
        });

        return () => {
            isMounted.current = false;
        };
    }, [startDate, endDate, addError, t, checkCarbonReports, mode]);

    return (
        <>
            <Helmet>
                <title>
                    {t('dashboard.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title
                marginTop={IsDesktop() ? 'none' : 'xs'}
                title={t('dashboard.title')}
                infoIcon={
                    <ModalIcon
                        title={t('dashboard.equivalence.modal.title')}
                        icon="QuestionBoldIcon"
                        iconSize="sm"
                    >
                        <CarbonEquivalenceModal
                            carbonData={convertCarbonReports(
                                reportsData,
                                'emitted',
                            )}
                        />
                    </ModalIcon>
                }
            >
                <ToggleMode />
            </H1Title>

            <Box marginBottom={IsDesktop() ? 'lg' : 'md'} style={{ width: 'max-content' }}>
                <InternalGrid
                    displayType="grid"
                    childrenMarginBottom={noDataMessage ? 'md' : null}
                    justifyItems="stretch"
                    alignItems="stretch"
                    gridTemplateColumns="1fr"
                    hasStaticWidth={IsDesktop() ? false : true}
                >
                    <DashboardFilter
                        setEndDate={setEndDate}
                        setStartDate={setStartDate}
                        startDate={startDate}
                        endDate={endDate}
                    />

                    {noDataMessage && <NoDataFoundMessage />}
                </InternalGrid>
            </Box>

            <div ref={ref}>
                <Grid
                    displayType="grid"
                    gridTemplateColumns={
                        width > ThemeDefault.breakpoint.lg ? '1fr 50%' : '100%'
                    }
                    gridGap="lg"
                    justifyItems="stretch"
                    childrenMargin="md"
                    childrenMarginBig="none"
                    blockPadding="none"
                    align="left"
                >
                    <DashboardCardCO2Emitted
                        reportsData={reportsData}
                        isLoading={isLoading}
                    />

                    <InternalGrid
                        displayType="grid"
                        gridTemplateColumns="100%"
                        gridGap="lg"
                        alignItems="stretch"
                        justifyItems="stretch"
                    >
                        <DashboardCardCO2Histogram
                            histogramData={histogramData}
                            isLoading={isLoading}
                            endDateValue={endDate}
                            startDateValue={startDate}
                        />
                    </InternalGrid>
                </Grid>
            </div>
        </>
    );
};

export default withTranslation('common')(Dashboard);
